/**
 * Enclosed regions ("faces") of the plan, found on demand by flood filling the
 * background bounded by line ink. Dashed outlines are closed by treating
 * pixels within `r` of ink as walls.
 */
import { simplify } from '../editor/geom.ts';
import type { VectorPlan } from './types.ts';
import { inkDistances } from './raster.ts';

export interface Face {
  r: number;
  area: number; // working px
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  mask: Uint8Array; // bbox-local region mask (bw × bh)
  bw: number;
  bh: number;
}

let scratchVisited: Uint8Array | null = null;
let scratchQueue: Int32Array | null = null;

/** Flood the background region containing (sx, sy) (working px) with walls where edt <= r. */
export function floodFace(plan: VectorPlan, sx: number, sy: number, r: number, maxPixels: number): Face | null {
  const { cw, ch } = plan;
  // (the distances are made in the worker after the plan; wanted before they come, they are made here)
  if (plan.edt.length !== cw * ch) plan.edt = inkDistances(plan.lineMask, cw, ch);
  const edt = plan.edt;
  const n = cw * ch;
  if (!scratchVisited || scratchVisited.length !== n) {
    scratchVisited = new Uint8Array(n);
    scratchQueue = new Int32Array(n);
  }
  const visited = scratchVisited;
  const queue = scratchQueue!;
  visited.fill(0);
  const start = sy * cw + sx;
  if (sx < 0 || sy < 0 || sx >= cw || sy >= ch || edt[start]! <= r) return null;
  let head = 0,
    tail = 0;
  queue[tail++] = start;
  visited[start] = 1;
  let minX = sx,
    minY = sy,
    maxX = sx,
    maxY = sy,
    count = 0;
  while (head < tail) {
    const p = queue[head++]!;
    count++;
    if (count > maxPixels) return null;
    const x = p % cw,
      y = (p - x) / cw;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    const push = (q: number) => {
      if (!visited[q] && edt[q]! > r) {
        visited[q] = 1;
        queue[tail++] = q;
      }
    };
    if (x > 0) push(p - 1);
    if (x < cw - 1) push(p + 1);
    if (y > 0) push(p - cw);
    if (y < ch - 1) push(p + cw);
  }
  const bw = maxX - minX + 1,
    bh = maxY - minY + 1;
  const mask = new Uint8Array(bw * bh);
  for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) mask[y * bw + x] = visited[(minY + y) * cw + minX + x]!;
  return { r, area: count, minX, minY, maxX, maxY, mask, bw, bh };
}

/**
 * Outer contour of a face (Moore neighbour tracing on the bbox-local mask),
 * returned as a flat polygon in image px, simplified.
 */
export function faceContour(face: Face, plan: VectorPlan, eps: number): number[] {
  const { mask, bw, bh } = face;
  const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < bw && y < bh ? mask[y * bw + x]! : 0);
  // find the top-left-most set pixel
  let sx = -1,
    sy = -1;
  outer: for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) if (mask[y * bw + x]) {
    sx = x;
    sy = y;
    break outer;
  }
  if (sx < 0) return [];
  // Moore-neighbour tracing with Jacob's stopping criterion (stop when the start pixel is
  // re-entered from the same direction), so boundaries that pass through the start twice
  // are traced completely.
  const N8 = [
    [-1, -1],
    [0, -1],
    [1, -1],
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
    [-1, 0],
  ] as const;
  const pts: number[] = [];
  let cx = sx,
    cy = sy;
  let bx = sx - 1,
    by = sy; // backtrack: the unset pixel we "came from" (left of start)
  const nextFrom = (x: number, y: number, bx0: number, by0: number): { nx: number; ny: number; pbx: number; pby: number } | null => {
    let bi = 0;
    for (let i = 0; i < 8; i++) if (x + N8[i]![0] === bx0 && y + N8[i]![1] === by0) bi = i;
    let prevX = bx0,
      prevY = by0;
    for (let i = 1; i <= 8; i++) {
      const d = N8[(bi + i) % 8]!;
      const nx = x + d[0],
        ny = y + d[1];
      if (at(nx, ny)) return { nx, ny, pbx: prevX, pby: prevY };
      prevX = nx;
      prevY = ny;
    }
    return null;
  };
  const first = nextFrom(cx, cy, bx, by);
  if (!first) return [(face.minX + sx + 0.5) / plan.k, (face.minY + sy + 0.5) / plan.k];
  const maxPts = 6 * (bw + bh) + 64;
  for (let step = 0; step < maxPts; step++) {
    pts.push(cx, cy);
    const n = nextFrom(cx, cy, bx, by);
    if (!n) break;
    // Jacob's criterion: back at the start and about to repeat the first move.
    if (cx === sx && cy === sy && step > 0 && n.nx === first.nx && n.ny === first.ny) break;
    bx = n.pbx;
    by = n.pby;
    cx = n.nx;
    cy = n.ny;
  }
  // to image px, offset to pixel centres, then expand outward by the wall radius so the
  // polygon sits on the bounding lines rather than inside them
  const k = plan.k;
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) out.push((face.minX + pts[i]! + 0.5) / k, (face.minY + pts[i + 1]! + 0.5) / k);
  const simp = simplify(out, eps);
  return expandPolygon(simp, (face.r + 0.5) / k);
}

/** Offset a simple polygon outward by d (miter-limited vertex normals). Clockwise or CCW agnostic. */
export function expandPolygon(pts: number[], d: number): number[] {
  const n = pts.length / 2;
  if (n < 3 || d === 0) return pts;
  // signed area to know orientation
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pts[2 * i]! * pts[2 * j + 1]! - pts[2 * j]! * pts[2 * i + 1]!;
  }
  const sign = area > 0 ? 1 : -1;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const p = (i + n - 1) % n,
      q = (i + 1) % n;
    const x = pts[2 * i]!,
      y = pts[2 * i + 1]!;
    const e1x = x - pts[2 * p]!,
      e1y = y - pts[2 * p + 1]!;
    const e2x = pts[2 * q]! - x,
      e2y = pts[2 * q + 1]! - y;
    const l1 = Math.hypot(e1x, e1y) || 1,
      l2 = Math.hypot(e2x, e2y) || 1;
    // outward normals (for CCW polygons in screen coords, outward = (dy, -dx) * sign)
    const n1x = (e1y / l1) * sign,
      n1y = (-e1x / l1) * sign;
    const n2x = (e2y / l2) * sign,
      n2y = (-e2x / l2) * sign;
    let bx = n1x + n2x,
      by = n1y + n2y;
    const bl = Math.hypot(bx, by);
    if (bl < 1e-6) {
      bx = n1x;
      by = n1y;
    } else {
      bx /= bl;
      by /= bl;
    }
    const cos = bx * n1x + by * n1y;
    const scale = Math.min(3, 1 / Math.max(0.33, cos));
    out.push(x + bx * d * scale, y + by * d * scale);
  }
  return out;
}
